import React, { useMemo, useState } from 'react';
import { useMutation } from 'convex/react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../convex/_generated/api';
import Layout from '../components/Layout/Layout';
import { useBetterAuthConvexAuth } from '../hooks/useAppAuth';
import { authClient } from '../lib/auth-client';


const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { convexUser } = useBetterAuthConvexAuth();
  const validateMyPassword = useMutation(api.users.validateMyPassword);
  const recordMyPasswordRotation = useMutation(api.users.syncPasswordHashFromBetterAuth);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const rules = useMemo(() => ({
    length: newPassword.length >= 12,
    upper: /[A-Z]/.test(newPassword),
    lower: /[a-z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    special: /[^A-Za-z0-9]/.test(newPassword),
    match: newPassword.length > 0 && newPassword === confirmPassword,
  }), [newPassword, confirmPassword]);

  const isValid = Object.values(rules).every(Boolean);

  const generatePassword = () => {
    const length = 16;
    const lower = 'abcdefghijklmnopqrstuvwxyz';
    const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const digits = '0123456789';
    const special = '!@#$%^&*';
    const all = lower + upper + digits + special;

    const pickRandom = (chars: string) => {
      const arr = new Uint32Array(1);
      crypto.getRandomValues(arr);
      return chars[arr[0] % chars.length];
    };

    // Guarantee at least one from each required category
    const guaranteed = [
      pickRandom(lower),
      pickRandom(upper),
      pickRandom(digits),
      pickRandom(special),
    ];

    // Fill remaining with random from full charset
    const remaining = length - guaranteed.length;
    const array = new Uint32Array(remaining);
    crypto.getRandomValues(array);
    const rest = Array.from(array, (x) => all[x % all.length]);

    // Combine and shuffle
    const combined = [...guaranteed, ...rest];
    for (let i = combined.length - 1; i > 0; i--) {
      const arr = new Uint32Array(1);
      crypto.getRandomValues(arr);
      const j = arr[0] % (i + 1);
      [combined[i], combined[j]] = [combined[j], combined[i]];
    }

    const generated = combined.join('');
    setNewPassword(generated);
    setConfirmPassword(generated);
    setShowPassword(true);
  };

  // ChangePasswordPage.tsx - replace handleSubmit body
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!isValid) {
      setError('Please satisfy all password rules before submitting.');
      return;
    }

    try {
      setSaving(true);

      const result = await validateMyPassword({
        newPassword: newPassword.trim(),
      });

      if (!result.success) {
        setError(result.error || 'Unable to change password.');
        return;
      }
      const authResult = await authClient.changePassword({
        currentPassword: currentPassword.trim(),
        newPassword: newPassword.trim(),
        revokeOtherSessions: true,
      });

      if (authResult.error) {
        setError("Unable to update password. Please try again.");
        return;
      }
      const record = await recordMyPasswordRotation({});
      if (!record.success) {
        setError(record.error || "Password changed, but local history update failed.");
        return;
      }
      setSuccess('Password updated successfully.');
      const target = convexUser?.role === 'customer' ? '/dashboard' : '/admin';
      setTimeout(() => navigate(target), 700);
    } catch (submitError: any) {
      setError(submitError.message || 'Unable to change password.');
    } finally {
      setSaving(false);
    }
  };


  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm p-6 sm:p-8">
          <h1 className="text-2xl font-medium font-sans text-gray-900 mb-2">Change Password</h1>
          <p className="text-gray-600 mb-6">
            Use a strong password with upper and lower case letters, numbers, and symbols.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                className="w-full"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">New Password</label>
                <button
                  type="button"
                  onClick={generatePassword}
                  className="text-sm bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700"
                >
                  Generate Password
                </button>
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                className="w-full"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                className="w-full"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>

            <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700 space-y-1">
              <p className={rules.length ? 'text-green-700' : ''}>At least 12 characters</p>
              <p className={rules.upper ? 'text-green-700' : ''}>One uppercase letter</p>
              <p className={rules.lower ? 'text-green-700' : ''}>One lowercase letter</p>
              <p className={rules.number ? 'text-green-700' : ''}>One number</p>
              <p className={rules.special ? 'text-green-700' : ''}>One special character</p>
              <p className={rules.match ? 'text-green-700' : ''}>Passwords match</p>
            </div>

            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-700">Show passwords</label>
              <input
                type="checkbox"
                checked={showPassword}
                onChange={(e) => setShowPassword(e.target.checked)}
              />
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}
            {success && <p className="text-sm text-green-700">{success}</p>}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!isValid || saving}
                className="px-4 py-2 rounded-md bg-[#16A34A]  text-white hover:bg-shade-green-700 disabled:opacity-50"
              >
                {saving ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default ChangePasswordPage;
